import {
  sampleDrawLayer,
  sampleLineLayer,
  samplePointLayer,
} from 'components/MapComponent/constants/sampleLayers';

import {
  MapMemo,
  SilurianProjectionResponse,
  HurricaneFeature,
  HurricanePointFeature,
  LayerConfiguration,
} from './mapEventsEffectTypes';
import { hurricaneLayerTypesToMapboxColorExpression } from './configureHurricaneLayers';
import {
  checkLayerExistsMemo,
  checkSourceExistsMemo,
} from './mapEventsEffectMemos';

const silurian_cones_layer_id = 'public.silurian_cones';
const silurian_points_layer_id = 'public.silurian_points';
const silurian_paths_layer_id = 'public.silurian_paths';
export const silurian_cones_source_id = 'public.silurian_cones_source_id';
export const silurian_points_source_id = 'public.silurian_points_source_id';
export const silurian_paths_source_id = 'public.silurian_paths_source_id';

type SilurianProcessedData = {
  silurianConesFeatures: HurricaneFeature[];
  silurianPointsFeatures: HurricanePointFeature[];
  silurianPathsFeatures: HurricaneFeature[];
};

export function processSilurianProjectionData(
  silurianProjectionData: SilurianProjectionResponse[]
): SilurianProcessedData {
  return silurianProjectionData.reduce(
    (acc: SilurianProcessedData, feature) => {
      const geometry = JSON.parse(feature.geojson);
      const formatted: HurricaneFeature = {
        type: 'Feature',
        geometry,
        properties: {
          ...feature,
          hazard_name: 'Hurricane',
          hazard_data_category: 'Hurricane',
          storm_data_category: 'Forecast',
          geometry_type: feature.type,
          instance_id: feature.storm_id_dt,
          properties: {
            STORMNAME: feature.properties.storm_name,
          },
        },
      };

      if (feature.type === 'Polygon') {
        acc.silurianConesFeatures.push(formatted);
      } else if (feature.type === 'Point') {
        const unifiedSpeed =
          (feature.weather_properties.vmax as unknown as number) * 1.944; // convert from m/s to knots

        const formattedPoint: HurricanePointFeature = {
          ...formatted,
          properties: {
            ...formatted.properties,
            unified_speed: unifiedSpeed,
            properties: {
              ...formatted.properties.properties,
              MAXWIND: unifiedSpeed,
            },
          },
        };

        acc.silurianPointsFeatures.push(formattedPoint);
      } else if (feature.type === 'LineString') {
        acc.silurianPathsFeatures.push(formatted);
      }

      return acc;
    },
    {
      silurianConesFeatures: [],
      silurianPointsFeatures: [],
      silurianPathsFeatures: [],
    }
  );
}

export function addSilurianProjectionLayers({
  silurianProjectionData,
  map,
  checkLayerToggle,
  addMapEventHandlers,
}: {
  addMapEventHandlers: (layerConfig: LayerConfiguration) => void;
  silurianProjectionData: SilurianProjectionResponse[];
  map: MapMemo;
  checkLayerToggle: (layerName: string) => boolean;
}) {
  if (checkLayerToggle('Hurricane') && silurianProjectionData.length > 0) {
    const {
      silurianConesFeatures,
      silurianPointsFeatures,
      silurianPathsFeatures,
    } = processSilurianProjectionData(silurianProjectionData);

    const checkSourceExists = checkSourceExistsMemo(map);

    if (!checkSourceExists(silurian_cones_source_id)) {
      map.current.addSource(silurian_cones_source_id, {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: silurianConesFeatures || [],
        },
      });
    }

    if (!checkSourceExists(silurian_points_source_id)) {
      map.current.addSource(silurian_points_source_id, {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: silurianPointsFeatures || [],
        },
      });
    }

    if (!checkSourceExists(silurian_paths_source_id)) {
      map.current.addSource(silurian_paths_source_id, {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: silurianPathsFeatures || [],
        },
      });
    }

    const hurricaneConesLayerConfig = {
      ...sampleDrawLayer,
      id: silurian_cones_layer_id,
      source: silurian_cones_source_id,
      generateId: true,
      paint: {
        ...sampleDrawLayer.paint,
        'fill-color': '#FFFFFF',
      },
      metadata: {
        eventlayer: true,
        usermade: undefined,
      },
    };

    const hurricanePathsLayerConfig = {
      ...sampleLineLayer,
      id: silurian_paths_layer_id,
      source: silurian_paths_source_id,
      generateId: true,
      paint: {
        ...sampleLineLayer.paint,
        'line-color': '#D3D3D3',
      },
      metadata: {
        eventlayer: true,
        usermade: undefined,
      },
    };

    const hurricanePointsLayerConfig = {
      ...samplePointLayer,
      id: silurian_points_layer_id,
      source: silurian_points_source_id,
      generateId: true,
      paint: {
        ...samplePointLayer.paint,
        'circle-color': hurricaneLayerTypesToMapboxColorExpression(),
        'circle-radius': 10,
      },
      metadata: {
        eventlayer: true,
        usermade: undefined,
      },
    };

    const checkLayerExists = checkLayerExistsMemo(map);

    [
      hurricaneConesLayerConfig,
      hurricanePathsLayerConfig,
      hurricanePointsLayerConfig,
    ].forEach((layerConfig) => {
      if (!checkLayerExists(layerConfig.id)) {
        addMapEventHandlers(layerConfig);
      }
    });
  }
}

export function removeSilurianProjectionLayers({
  removeLayer,
  removeSource,
}: {
  removeLayer: (layerId: string) => void;
  removeSource: (sourceId: string) => void;
}) {
  removeLayer(silurian_cones_layer_id);
  removeLayer(silurian_paths_layer_id);
  removeLayer(silurian_points_layer_id);
  removeSource(silurian_cones_source_id);
  removeSource(silurian_paths_source_id);
  removeSource(silurian_points_source_id);
}

export function setSilurianProjectionLayersVisibility({
  setLayerVisibility,
  isVisible,
}: {
  setLayerVisibility: (layerId: string, isVisible: boolean) => void;
  isVisible: boolean;
}) {
  setLayerVisibility(silurian_cones_layer_id, isVisible);
  setLayerVisibility(silurian_paths_layer_id, isVisible);
  setLayerVisibility(silurian_points_layer_id, isVisible);
}
